import type { SocialBet } from 'src/types/domain/SocialBet'

/**
 * Pure presentation logic for the crash social bet panel (ASK 5).
 *
 * Kept free of React / store / i18n so the row ordering, the collapsed-pill
 * summary and the two-focus state machine can be unit-tested in isolation.
 * The component is a thin shell over these functions.
 */

export type SocialFocus = 'stream' | 'social'

/** Whether a bet counts as a realised win (payout credited to the player). */
export function isWinningBet(status: SocialBet['status']): boolean {
  return status === 'won' || status === 'cashed_out'
}

/**
 * Rows for the list, sorted by stake DESC (biggest bets read first — the
 * Aviator/Evolution convention). Stable for equal stakes via a createdAt
 * tiebreak (newest first) so the order does not jitter as statuses flip.
 */
export function sortSocialBets(betsById: Record<string, SocialBet>): SocialBet[] {
  return Object.values(betsById).sort((a, b) => {
    if (b.betAmount !== a.betAmount) return b.betAmount - a.betAmount
    return b.createdAt < a.createdAt ? -1 : b.createdAt > a.createdAt ? 1 : 0
  })
}

export interface SocialPanelSummary {
  /** Number of bets in the current round. */
  count: number
  /** Sum of stakes across the current round (wire units, pre display-scale). */
  totalStaked: number
}

/** The collapsed-pill summary: how many bets and the total staked this round. */
export function summariseSocialBets(betsById: Record<string, SocialBet>): SocialPanelSummary {
  let totalStaked = 0
  let count = 0
  for (const id in betsById) {
    const bet = betsById[id]
    if (!bet) continue
    count += 1
    totalStaked += bet.betAmount
  }
  return { count, totalStaked }
}

/**
 * How a row's status should be read when totalling.
 *
 * B41 — Birdie's outcome resolves as the balls drop, so a row can be DECIDED
 * while the store still calls it pending, and the running total has to be able
 * to see that. Every other game passes `ownStatus` (the default) and totals
 * exactly what it totalled before.
 *
 * Note what this does NOT change: the money. A resolved win is still valued at
 * `actualPayout ?? potentialPayout`, so the figure switches to the server's own
 * number the moment the server sends one.
 */
export type BetStatusResolver = (bet: SocialBet) => SocialBet['status']

const ownStatus: BetStatusResolver = (bet) => bet.status

/** Total credited to players from the previous round (won + cashed-out). */
export function sumWonPayouts(
  betsById: Record<string, SocialBet>,
  statusOf: BetStatusResolver = ownStatus
): number {
  let total = 0
  for (const id in betsById) {
    const bet = betsById[id]
    if (!bet) continue
    if (!isWinningBet(statusOf(bet))) continue
    total += bet.actualPayout ?? bet.potentialPayout
  }
  return total
}

/** How many bets in a round landed a win (won + cashed-out) — the winners count. */
export function countWinners(
  betsById: Record<string, SocialBet>,
  statusOf: BetStatusResolver = ownStatus
): number {
  let count = 0
  for (const id in betsById) {
    const bet = betsById[id]
    if (!bet) continue
    if (isWinningBet(statusOf(bet))) count += 1
  }
  return count
}

// ── v16: the pill / top-bar readout adapts to the round phase ────────────
//
// A COARSE crash round phase, derived from the latest race event type — just
// enough for the pill/top-bar to decide what to show. The betting default is
// the safe stage-setter (a fresh round with no events yet reads as betting).
export type CrashRoundPhase = 'betting' | 'live' | 'crashed' | 'settled'

export function resolveCrashRoundPhase(latestEventType: string | undefined): CrashRoundPhase {
  switch (latestEventType) {
    case 'race_started':
      return 'live'
    case 'race_crashed':
    case 'race_ended':
      return 'crashed'
    case 'race_settled':
    case 'race_cancelled':
      return 'settled'
    // race_ready / race_starting / race_scheduled / undefined → the betting
    // stage-setter (openable early, before any climb).
    default:
      return 'betting'
  }
}

/**
 * What the pill / top-bar summary reads for a phase:
 *   staked — during betting + climbing: total STAKED this round + live count.
 *   won    — once crashed / settled: total WON this round + winners count
 *            (players check who won the round, not just who staked).
 */
export type PillMode = 'staked' | 'won'

export function resolvePillMode(phase: CrashRoundPhase): PillMode {
  return phase === 'crashed' || phase === 'settled' ? 'won' : 'staked'
}

/**
 * v17 STAY-OPEN CONTRACT.
 *
 * v16 snapped an open social view back to the stream on every new round uuid
 * (`shouldResetFocus`). v17 deliberately removes that: a viewer who switched to
 * the social view STAYS there across round transitions — only tapping the
 * 'Stream' affordance returns to the stream. This pure predicate encodes the
 * new rule so the "round change must NOT close the view" behaviour is unit-
 * testable: a round-id change (or any churn) never derives a close.
 *
 * `focusAfterRoundChange` always returns the CURRENT focus unchanged — the
 * shared flag is owned by the store and only user intent (open/close) mutates
 * it. It exists so the container's round-transition effect has a single,
 * tested source of truth for "what should focus be after this round change".
 */
export function focusAfterRoundChange(currentFocus: SocialFocus): SocialFocus {
  return currentFocus
}

/**
 * The two-focus toggle (mobile). `stream` = the betting/stream stays the
 * focus and the feed is a small pill; `social` = the bets list is the focus
 * and the real stream shrinks to the top-right corner square. Tapping either
 * surface flips to the other.
 */
export function toggleFocus(current: SocialFocus): SocialFocus {
  return current === 'stream' ? 'social' : 'stream'
}

// ── v20: honest phase + the bail-out meter ────────────────────────────────

/**
 * Gate the panel's crash transition on THIS VIEWER'S FRAMES (v20).
 *
 * The event clock leads the video by syncDelta (~1.5-2s). Flipping the
 * panel's 'Crashed' caption / won-mode on the event clock announced the
 * round's end seconds before the footage showed it — the exact early-tell
 * class every other crash surface gates via useVisualCrash. While the crash
 * is known backend-side but not yet visible ('armed'), the panel stays
 * 'live' (its display-clock number is frozen at the held value, exactly like
 * the hero). 'undriven' (no visual driver mounted) falls back to the event
 * clock per that status's contract.
 */
export function gateCrashPhaseOnVisual(
  phase: CrashRoundPhase,
  visualStatus: 'undriven' | 'armed' | 'crashed',
  crashKnown: boolean
): CrashRoundPhase {
  // Hold ONLY while an actual crash is in flight toward the viewer's frames.
  // A CANCELLED round reaches 'settled' with NO crash event — the visual
  // store then stays 'armed' until the next round, and holding on it would
  // wedge the panel in 'live' for the whole cancel gap (review C2).
  if (!crashKnown) return phase
  if ((phase === 'crashed' || phase === 'settled') && visualStatus === 'armed') return 'live'
  return phase
}

/**
 * The BAIL-OUT METER — the header band's single source of truth (v23).
 *
 * ONE round, always money WON, ALWAYS the CURRENT round (Kasper: the old
 * figures must clear the moment the next round starts — the meter "just
 * shows as total won always and starts at 0"):
 *
 *   betting — the fresh round: 0/N bets, $0.00 won. The previous round's
 *             figures are GONE (the store rotates allBets → empty at the new
 *             race), so a new round never shows stale totals.
 *   live    — the same current round, counting up with every cashout;
 *             progress = cashed/total.
 *   crashed/settled — frozen on this round's final figures (which stay up
 *             through the crash payoff, then clear when the next round's
 *             betting opens — current resets to 0 and the hero snaps down).
 *
 * The previous TAB shows the settled prior round (frozenMeterFor). Callers
 * MUST pass the visual-gated phase (gateCrashPhaseOnVisual) so the freeze
 * can never lead the footage.
 */
export interface RoundTotals {
  count: number
  winnersCount: number
  wonTotal: number
}

export interface BetMeter {
  mode: 'wins' | 'frozen'
  /** Total won for the round. */
  heroValue: number
  cashedCount: number
  totalCount: number
  /** Cashed-out share of the round's bets, 0..1 (the progress fill). */
  progress: number
}

function meterFor(totals: RoundTotals, mode: BetMeter['mode']): BetMeter {
  return {
    mode,
    heroValue: totals.wonTotal,
    cashedCount: totals.winnersCount,
    totalCount: totals.count,
    progress: totals.count > 0 ? Math.min(1, Math.max(0, totals.winnersCount / totals.count)) : 0
  }
}

export function resolveBetMeter(input: { phase: CrashRoundPhase; current: RoundTotals }): BetMeter {
  const frozen = input.phase === 'crashed' || input.phase === 'settled'
  return meterFor(input.current, frozen ? 'frozen' : 'wins')
}

/** The previous tab's meter: that round's settled story, frozen. */
export function frozenMeterFor(totals: RoundTotals): BetMeter {
  return meterFor(totals, 'frozen')
}

/**
 * The header avatar cluster: the round's biggest stakes, biggest first
 * (undefined name = anonymous, still occupies its slot). Carries country too
 * so the cluster's generated disc matches the same player's row disc.
 * Reuses sortSocialBets so the cluster always agrees with the list ordering.
 */
export interface StakerIdentity {
  name: string | undefined
  country: string | undefined
}

export function topStakers(betsById: Record<string, SocialBet>, n: number): StakerIdentity[] {
  return sortSocialBets(betsById)
    .slice(0, n)
    .map((bet) => ({ name: bet.playerDisplayName, country: bet.playerCountry }))
}

/**
 * What status a row DISPLAYS on a given tab (v20/v21/v24).
 *
 * No live 'lost' event exists on the social channel — bets copied into the
 * previous round at the transition keep 'pending' forever. On the PREVIOUS
 * tab that is a lie by omission: the round is settled, an uncashed bet lost.
 * EXCEPT a CANCELLED round (review C3): its uncashed bets were REFUNDED, so
 * they display neutral 'cancelled', never LOST.
 *
 * v24 (Kasper note a): the CURRENT tab tells the same truth the moment the
 * crash is on the viewer's frames — `currentRoundCrashed` MUST come from the
 * visual-gated phase (crashKnown && crashed/settled), never the raw event
 * clock, or the LOST labels would announce the bust seconds early. A
 * cancelled round never sets it (crashKnown stays false), so refunded bets
 * stay neutral here too.
 */
export function displayStatusForTab(
  status: SocialBet['status'],
  tab: 'current' | 'previous',
  previousRoundCancelled = false,
  currentRoundCrashed = false
): SocialBet['status'] {
  if (tab === 'previous' && status === 'pending') {
    return previousRoundCancelled ? 'cancelled' : 'lost'
  }
  if (tab === 'current' && status === 'pending' && currentRoundCrashed) {
    return 'lost'
  }
  return status
}

/**
 * The compact top-bar / hero-parity value line for a phase (pure, tested):
 *   betting — the countdown string (e.g. '0:08'), or '' if none.
 *   live    — the climbing multiplier formatted '2.41x' (number THEN x —
 *             the standard multiplier convention the hero uses, `${m}x`).
 *   crashed — '3.17x' (the crash multiplier), or '' if unknown.
 *   settled — the countdown to the next round (e.g. '0:05'), or ''.
 *
 * Multiplier values are DISPLAY-only (the hero's beacon clock) — never money.
 * A nullish multiplier during live falls back to the flat '1.00x' so the line
 * never blanks while the clock warms up.
 */
export function formatTopBarValue(input: {
  phase: CrashRoundPhase
  multiplier?: number | null
  crashMultiplier?: number | null
  countdownText?: string | null
}): string {
  switch (input.phase) {
    case 'betting':
      return input.countdownText ?? ''
    case 'live': {
      const m = input.multiplier != null && input.multiplier >= 1 ? input.multiplier : 1
      return `${m.toFixed(2)}x`
    }
    case 'crashed':
      return input.crashMultiplier != null ? `${input.crashMultiplier.toFixed(2)}x` : ''
    case 'settled':
      return input.countdownText ?? ''
    default:
      return ''
  }
}
